import { Pill, Text, Title } from "@clipboard-health/ui-components";
import { isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { parseISO } from "date-fns";

import { PageHeaderTopRow } from "../../../components/PageHeaderTopRow";
import { formatDayOfMonthWithDayName } from "../../../utils/formatDayOfMonthWithDayName";
import { type OpenBundleShift, type ShiftBundle } from "../types";
import { getBundleLabel } from "../utils";

interface ShiftBundleBottomSheetHeaderProps {
  bundle: ShiftBundle;
  sortedBundleShifts: OpenBundleShift[];
  qualificationText: string;
  workplaceTimezone?: string;
  onClose: () => void;
}

function formatBundleDateRange(sortedShifts: OpenBundleShift[], timezone?: string): string {
  const [firstShift] = sortedShifts;
  const lastShift = sortedShifts.at(-1);
  if (isNil(firstShift) || isNil(lastShift)) {
    return "";
  }

  const firstDate = parseISO(firstShift.attributes.start);
  const lastDate = parseISO(lastShift.attributes.start);

  const firstFormatted = formatDayOfMonthWithDayName(firstDate, { timezone });
  const lastFormatted = formatDayOfMonthWithDayName(lastDate, { timezone });

  return `${firstFormatted} - ${lastFormatted}`;
}

function getBundlePillLabel(bundle: ShiftBundle): string {
  const { requiredShiftCount, totalShiftCount } = bundle;

  if (requiredShiftCount === totalShiftCount) {
    return `Book ${requiredShiftCount} of ${totalShiftCount} shifts`;
  }

  return `Book at least ${requiredShiftCount} of ${totalShiftCount} shifts`;
}

export function ShiftBundleBottomSheetHeader(props: ShiftBundleBottomSheetHeaderProps) {
  const { bundle, sortedBundleShifts, qualificationText, workplaceTimezone, onClose } = props;

  const dateRange = formatBundleDateRange(sortedBundleShifts, workplaceTimezone);

  return (
    <Stack
      sx={(theme) => ({
        paddingTop: 6,
        paddingBottom: 7,
        paddingX: 5,
        background: theme.palette.shift?.bundle?.gradient,
      })}
      spacing={4}
    >
      <PageHeaderTopRow sx={{ paddingX: 0, paddingBottom: 3 }} onClickBack={onClose} />

      <Stack sx={{ paddingLeft: 3 }} spacing={4}>
        <Text semibold variant="body1">
          {qualificationText} &middot; {dateRange}
        </Text>

        <Title variant="h3" component="h2">
          {getBundleLabel(bundle.requiredShiftCount, bundle.totalShiftCount)}
        </Title>

        <Pill
          size="small"
          variant="filled"
          label={getBundlePillLabel(bundle)}
          iconType="block-shift"
          sx={(theme) => ({
            width: "fit-content",
            backgroundColor: theme.palette.common.white,
          })}
        />
      </Stack>
    </Stack>
  );
}
